import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ConfigHelper } from './helpers/config.helper';

import {
  AdoptChildAccountRequest,
  AffiliateLinkDto,
  AffiliateLinkReportRequestDto,
  AgreeServiceTermRequest,
  ApplyForBusinessAccountRequest,
  BusinessAccountDto,
  BusinessAccountWithChildrenDto,
  ChangeUserPasswordRequest,
  ChangeUserPasswordWhenSignedInRequest,
  CreateAccountInitializationInfoDto,
  CreateUserAccountRequest,
  DeliveryMethodDto,
  GeneratePasswordResetTokenRequest,
  Guid,
  MarketingAdDto,
  PretestRefundDiscountDto,
  InvoiceDetailDto,
  NotificationConfigDto,
  PersonalLookupInfoForTestRegistrationDto,
  UserAccountRegActionDto,
  UserAccountNotificationSettingsDto,
  UserAccountRegDto,
  ResultWrapper,
  ScoreReportDto,
  SaveChildUserAccountIdentityInfoRequest,
  ServiceTermDto,
  SurveyAnswersRequest,
  SurveyFormDetailsDto,
  UserAccountAddressDto,
  UserAccountDto,
  UserAccountHomeDto,
  UserAccountIdentityInfoDto,
  UserOrderDetailDto,
  ChildAccountOrderAddressRequest,
  UserOrderAddressDto,
  UserOrderDto,
  ValidateSuperSignInDto,
  TestSessionForRegistrationDto,
  RegTransferPaymentDto,
  UserTestRegistrationTransferRequest,
  WebDeliveryTestTakerStateDto,
  ScoreRecipientExpressShippingDto,
  EssaySubmissionRequest
} from '../dtos/dto';
import { StorageHelper } from './helpers/storage.helper';

@Injectable({
  providedIn: 'root'
})

export class AccountService {

  constructor(
    private http: HttpClient,
    private configHelper: ConfigHelper,
    private storageHelper: StorageHelper
  ) { }


  getMyAccountInfo(): Observable<ResultWrapper<UserAccountHomeDto>> {
    return this.http.get<ResultWrapper<UserAccountHomeDto>>(this.configHelper.getApiUrl("User/Account/Home"));
  }

  getBusinessAccount(): Observable<ResultWrapper<BusinessAccountWithChildrenDto>> {
    const params = new HttpParams();
    return this.http.get<ResultWrapper<BusinessAccountWithChildrenDto>>(this.configHelper.getApiUrl("User/Account/BusinessAccount"), { params });
  }

  editBusinessAccount(request: BusinessAccountDto): Observable<ResultWrapper<string>> {
    return this.http.put<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/BusinessAccount"), request);
  }

  isActiveBusinessAccount(): Observable<ResultWrapper<boolean>> {
    const params = new HttpParams();
    return this.http.get<ResultWrapper<boolean>>(this.configHelper.getApiUrl("User/Account/IsActiveBusinessAccount"), { params });
  }

  getAddress(): Observable<ResultWrapper<UserAccountAddressDto>> {
    return this.http.get<ResultWrapper<UserAccountAddressDto>>(this.configHelper.getApiUrl("User/Account/UserAddress"));
  }

  editAddress(request: UserAccountAddressDto): Observable<ResultWrapper<UserAccountAddressDto>> {
    return this.http.put<ResultWrapper<UserAccountAddressDto>>(this.configHelper.getApiUrl("User/Account/UserAddress"), request);
  }

  getOrderDetail(userOrderId: number): Observable<ResultWrapper<UserOrderDetailDto>> {
    return this.http.get<ResultWrapper<UserOrderDetailDto>>(this.configHelper.getApiUrl("UserOrder/Detail/" + userOrderId));
  }

  createUserAccount(request: CreateUserAccountRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("UserAccounts"), request);
  }

  sendActivationEmail(email: string): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/SendActivationEmail") + "?email=" + encodeURIComponent(email), {});
  }

  activateAccount(token: string): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/Activate") + "?token=" + token, {});
  }

  getNotificationSettings(): Observable<ResultWrapper<UserAccountNotificationSettingsDto>> {
    return this.http.get<ResultWrapper<UserAccountNotificationSettingsDto>>(this.configHelper.getApiUrl("User/Account/NotificationSettings"));
  }

  saveNotificationSettings(request: UserAccountNotificationSettingsDto): Observable<UserAccountNotificationSettingsDto> {
    return this.http.post<UserAccountNotificationSettingsDto>(this.configHelper.getApiUrl("User/Account/NotificationSettings"), request);
  }

  checkEligibility(): Observable<ResultWrapper<boolean>> {
    return this.http.get<ResultWrapper<boolean>>(this.configHelper.getApiUrl("User/Account/EligibilityForScholarship/"));
  }

  submitEssay(formData: FormData, request: EssaySubmissionRequest): Observable<ResultWrapper<string>> {
    const params = new HttpParams().set("institutionName", request.institutionName).set("programType", request.programType);
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/SubmitEssay"), formData, { params });
  }

  sendTestConfirmationEmail(regId: number): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/SendTestConfirmationEmail") + "?regId=" + regId, {});
  }

  getRegistrationDetails(regId: number): Observable<ResultWrapper<UserAccountRegDto>> {
    const params = new HttpParams().append('regId', regId.toString());
    return this.http.get<ResultWrapper<UserAccountRegDto>>(this.configHelper.getApiUrl("User/Reg/TestInfo/"), { params });
  }

  getChildAccountIdentityInfo(childUserAccountId: number): Observable<ResultWrapper<UserAccountIdentityInfoDto>> {
    const params = new HttpParams().append('childUserAccountId', childUserAccountId.toString());
    return this.http.get<ResultWrapper<UserAccountIdentityInfoDto>>(this.configHelper.getApiUrl("User/Account/ChildAccountIdentityInfo"), { params });
  }

  getChildAccountAddress(childUserAccountId: number): Observable<ResultWrapper<UserOrderAddressDto>> {
    const params = new HttpParams().append('childUserAccountId', childUserAccountId.toString());
    return this.http.get<ResultWrapper<UserOrderAddressDto>>(this.configHelper.getApiUrl("UserOrder/ChildAccountAddress"), { params });
  }

  editChildAccountAddress(request: ChildAccountOrderAddressRequest): Observable<ResultWrapper<string>> {
    return this.http.put<ResultWrapper<string>>(this.configHelper.getApiUrl("UserOrder/ChildAccountAddress"), request);
  }

  saveRegIdentityInfo(request: SaveChildUserAccountIdentityInfoRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/ChildAccountIdentityInfo"), request);
  }

  getNotificationConfig(): Observable<ResultWrapper<NotificationConfigDto>> {
    const params = new HttpParams();
    return this.http.get<ResultWrapper<NotificationConfigDto>>(this.configHelper.getApiUrl("User/Account/GetBusinessAccountNotificationConfig"), { params });
  }

  saveNotificationConfig(request: NotificationConfigDto): Observable<ResultWrapper<NotificationConfigDto>> {
    return this.http.post<ResultWrapper<NotificationConfigDto>>(this.configHelper.getApiUrl("User/Account/SaveBusinessAccountNotificationConfig"), request);
  }

  getBusinessAccountSurvey(): Observable<ResultWrapper<SurveyFormDetailsDto>> {
    return this.http.get<ResultWrapper<SurveyFormDetailsDto>>(this.configHelper.getApiUrl("User/Account/BusinessAccountSurvey"), {});
  }

  saveBusinessAccountSurvey(request: SurveyAnswersRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/BusinessAccountSurvey"), request);
  }

  getAuthToken(username: string, password: string, unlockToken?: string): Observable<AuthModel> {
    const body = new URLSearchParams();
    body.set('userName', username);
    body.set('password', password);
    body.set('grant_type', 'password');
    body.set('unlockToken', unlockToken);

    const options = {
      headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded'),
      withCredentials: false
    };
    return this.http.post<AuthModel>(`${this.configHelper.getBaseUrl()}/token`, body.toString(), options);
  }

  checkBusinessAccountFromEmail(email: string) {
    const params = new HttpParams()
      .set('userName', email);
    return this.http.get<ResultWrapper<boolean>>(this.configHelper.getApiUrl("IsBusinessAccount"), { params });
  }

  changePasswordSignedIn(request: ChangeUserPasswordWhenSignedInRequest): Observable<ResultWrapper<UserAccountDto>> {
    return this.http.post<ResultWrapper<UserAccountDto>>(this.configHelper.getApiUrl("ChangePassword"), request);
  }

  sendForgetPasswordEmail(request: GeneratePasswordResetTokenRequest): Observable<ResultWrapper<Guid>> {
    return this.http.post<ResultWrapper<Guid>>(this.configHelper.getApiUrl("ForgotPassword"), request);
  }

  resetPassword(request: ChangeUserPasswordRequest): Observable<ResultWrapper<UserAccountDto>> {
    return this.http.post<ResultWrapper<UserAccountDto>>(this.configHelper.getApiUrl("ResetPassword"), request);
  }

  getServiceTerms(serviceTermId: number): Observable<ResultWrapper<ServiceTermDto>> {
    const params = new HttpParams()
      .set('serviceTermId', serviceTermId.toString());
    return this.http.get<ResultWrapper<ServiceTermDto>>(this.configHelper.getApiUrl("ServiceTerm"), { params });
  }

  isUserAccountServiceTermActive(): Observable<ResultWrapper<boolean>> {
    return this.http.get<ResultWrapper<boolean>>(this.configHelper.getApiUrl("User/IsAccountServiceTermActive"));
  }

  getUpcomingRegistration(): Observable<ResultWrapper<UserAccountRegDto>> {
    return this.http.get<ResultWrapper<UserAccountRegDto>>(this.configHelper.getApiUrl("User/UpcomingRegistration"));
  }

  getActiveServiceTerm(): Observable<ResultWrapper<ServiceTermDto>> {
    return this.http.get<ResultWrapper<ServiceTermDto>>(this.configHelper.getApiUrl("ActiveServiceTerm"));
  }

  agreeServiceTerm(request: AgreeServiceTermRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/AgreeServiceTerm"), request);
  }

  getAccountIntializationInfo(): Observable<ResultWrapper<CreateAccountInitializationInfoDto>> {
    return this.http.get<ResultWrapper<CreateAccountInitializationInfoDto>>(this.configHelper.getApiUrl("UserAccount"), {});
  }

  validateProductKey(code: string): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/ValidateRedemptionCodes") + "?code=" + code, {});
  }

  redeemProductKey(code: string): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/RedemptionCodes") + "?code=" + code, {});
  }

  requestPretestDiscountCodeWithRegId(pretestRegId: number): Observable<ResultWrapper<PretestRefundDiscountDto>> {
    return this.http.post<ResultWrapper<PretestRefundDiscountDto>>(this.configHelper.getApiUrl("User/Account/RequestPretestDiscountCode") + "?pretestRegId=" + pretestRegId, {});
  }

  getBusinessAccountIndustries(): Observable<ResultWrapper<string[]>> {
    return this.http.get<ResultWrapper<string[]>>(this.configHelper.getApiUrl("BusinessAccountIndustries"), {});
  }

  getSalutations(): Observable<ResultWrapper<string[]>> {
    return this.http.get<ResultWrapper<string[]>>(this.configHelper.getApiUrl("Salutations"), {});
  }

  applyForBusinessAccount(request: ApplyForBusinessAccountRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("ApplyForBusinessAccount"), request);
  }

  checkScores(regId: number): Observable<ResultWrapper<ScoreReportDto>> {
    const params = new HttpParams().set('RegId', regId.toString());
    return this.http.get<ResultWrapper<ScoreReportDto>>(this.configHelper.getApiUrl("User/Reg/CheckScore"), { params });
  }

  getScoreReportFilename(regId: number): Observable<ResultWrapper<string>> {
    const params = new HttpParams().set('regId', regId.toString());
    return this.http.get<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/GetScoreReportFilename"), { params });
  }

  downloadScoreReport(filename: string): Observable<Blob> {
    return this.http.get(this.configHelper.getApiUrl(`User/Account/DownloadScoreReport?filename=${filename}`), { responseType: 'blob' });
  }

  getRegActions(regId: number): Observable<ResultWrapper<UserAccountRegActionDto>> {
    const params = new HttpParams().set('regId', regId.toString());
    return this.http.get<ResultWrapper<UserAccountRegActionDto>>(this.configHelper.getApiUrl("User/Reg/AvailableActions"), { params });
  }

  getMarketingAd(): Observable<ResultWrapper<MarketingAdDto>> {
    return this.http.get<ResultWrapper<MarketingAdDto>>(this.configHelper.getApiUrl("MarketingAd"));
  }

  getMarketingAdImageSrc(imageId: number) {
    return this.configHelper.getApiUrl("MarketingAdImage") + "?marketingAdImageId=" + imageId;
  }

  downloadCelpipScoreReport(regId: number): Observable<Blob> {
    return this.http.get(this.configHelper.getApiUrl(`User/Account/DownloadCelpipScoreReport?RegId=${regId}`), { responseType: 'blob' });
  }

  checkChildScores(regId: number, childAccountId: number): Observable<ResultWrapper<ScoreReportDto>> {
    const params = new HttpParams()
      .set('RegId', regId.toString())
      .set('childAccountId', childAccountId.toString());
    return this.http.get<ResultWrapper<ScoreReportDto>>(this.configHelper.getApiUrl("User/Reg/CheckScoreChild"), { params });
  }

  getSurvey(): Observable<ResultWrapper<SurveyFormDetailsDto>> {
    const params = new HttpParams()
      .set('testTypeId', "1")
      .set('testPurposeId', "1");
    return this.http.get<ResultWrapper<SurveyFormDetailsDto>>(this.configHelper.getApiUrl("SurveyForms"), { params });
  }

  adoptChildAccount(request: AdoptChildAccountRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/AdoptChildAccount"), request);
  }

  adoptionRequestInformation(token: string): Observable<ResultWrapper<string>> {
    return this.http.get<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/AdoptionRequestInformation") + "?encryptedToken=" + token);
  }

  getAdoptionServiceTerm(): Observable<ResultWrapper<string>> {
    return this.http.get<ResultWrapper<string>>(this.configHelper.getApiUrl("AdoptionAcceptanceServiceTerm"));
  }

  acceptAccountAdoption(token: string): Observable<ResultWrapper<string>> {
    return this.http.get<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/AcceptAccountAdoption") + "?encryptedToken=" + token);
  }

  getAffiliateSummary(): Observable<ResultWrapper<AffiliateLinkDto[]>> {
    return this.http.get<ResultWrapper<AffiliateLinkDto[]>>(this.configHelper.getApiUrl("Affiliate/Summary"), {});
  }

  getAffiliateDeliveryMethods(): Observable<ResultWrapper<DeliveryMethodDto[]>> {
    return this.http.get<ResultWrapper<DeliveryMethodDto[]>>(this.configHelper.getApiUrl("Affiliate/DeliveryMethods"), {});
  }

  getAffiliateDetail(affiliateLinkId: string): Observable<ResultWrapper<AffiliateLinkDto>> {
    const params = new HttpParams()
      .set('affiliateLinkId', affiliateLinkId.toString());
    return this.http.get<ResultWrapper<AffiliateLinkDto>>(this.configHelper.getApiUrl("Affiliate/Detail"), { params });
  }

  createAffiliateLink(request: AffiliateLinkDto): Observable<ResultWrapper<AffiliateLinkDto>> {
    return this.http.post<ResultWrapper<AffiliateLinkDto>>(this.configHelper.getApiUrl("Affiliate/Create"), request);
  }

  updateAffiliateLink(request: AffiliateLinkDto): Observable<ResultWrapper<AffiliateLinkDto>> {
    return this.http.post<ResultWrapper<AffiliateLinkDto>>(this.configHelper.getApiUrl("Affiliate/Update"), request);
  }

  getAffiliateReport(request: AffiliateLinkReportRequestDto): Observable<ResultWrapper<AffiliateLinkDto[]>> {
    return this.http.post<ResultWrapper<AffiliateLinkDto[]>>(this.configHelper.getApiUrl("Affiliate/Report"), request);
  }

  getInvoicesDetailByBusinessId(businessId: number): Observable<ResultWrapper<InvoiceDetailDto[]>> {
    const params = new HttpParams()
      .set('businessAccountId', businessId.toString());
    return this.http.get<ResultWrapper<InvoiceDetailDto[]>>(this.configHelper.getApiUrl("User/Account/IssuedInvoiceGetDetail"), { params });
  }

  getInvoicePdfNameById(invoiceId: number): Observable<ResultWrapper<string>> {
    const params = new HttpParams()
      .set('invoiceId', invoiceId.toString());
    return this.http.get<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/GetInvoicePdfName"), { params });
  }

  downloadInvoicePdf(invoiceName: string): Observable<Blob> {
    const params = new HttpParams()
      .set('filename', invoiceName.toString());
    const headers = new HttpHeaders().set('Content-Type', 'text').set('responseType', 'text');
    const options = {
      params,
      headers,
      responseType: 'blob' as 'json',
    };
    return this.http.get<Blob>(this.configHelper.getApiUrl("User/Account/DownloadInvoicePdf"), options);
  }

  getOrdersByInvoiceId(invoiceId: number): Observable<ResultWrapper<UserOrderDto[]>> {
    return this.http.get<ResultWrapper<UserOrderDto[]>>(this.configHelper.getApiUrl("UserOrder/Invoice/") + invoiceId.toString(), {});
  }

  validateSuperSignin(request: ValidateSuperSignInDto): Observable<ResultWrapper<boolean>> {
    return this.http.post<ResultWrapper<boolean>>(this.configHelper.getApiUrl("User/Account/ValidateSuperSignIn"), request);
  }

  getChildAccountRegs(): Observable<ResultWrapper<UserAccountRegDto[]>> {
    return this.http.get<ResultWrapper<UserAccountRegDto[]>>(this.configHelper.getApiUrl("User/Account/ChildUserAccountRegs"));
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  disownChildAccount(childUserAccountId: number): Observable<Object> {
    const url = this.configHelper.getApiUrl("User/Account/ChildAccount/" + childUserAccountId);
    return this.http.delete(url);
  }

  createChildUserAccount(request: CreateUserAccountRequest): Observable<ResultWrapper<number>> {
    return this.http.post<ResultWrapper<number>>(this.configHelper.getApiUrl("User/Account/CreateChildAccount"), request);
  }

  updateChildAccountAddress(request: ChildAccountOrderAddressRequest): Observable<ResultWrapper<string>> {
    return this.http.put<ResultWrapper<string>>(this.configHelper.getApiUrl("UserOrder/ChildAccountAddress"), request);
  }

  updateChildAccountIdentityInfo(request: SaveChildUserAccountIdentityInfoRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Account/ChildAccountIdentityInfo"), request);
  }

  getPersonalLookupInfo(): Observable<ResultWrapper<PersonalLookupInfoForTestRegistrationDto>> {
    return this.http.get<ResultWrapper<PersonalLookupInfoForTestRegistrationDto>>(this.configHelper.getApiUrl("User/Account/PersonalLookupInfo"), {});
  }

  requestCancellation(regId: number): Observable<ResultWrapper<UserAccountRegDto>> {
    const params = new HttpParams().append('regId', regId.toString());
    return this.http.get<ResultWrapper<UserAccountRegDto>>(this.configHelper.getApiUrl("User/Reg/RequestCancel/"), { params });
  }

  checkCancellationRequested(regId: number): Observable<ResultWrapper<boolean>> {
    const params = new HttpParams().append('regId', regId.toString());
    return this.http.get<ResultWrapper<boolean>>(this.configHelper.getApiUrl("User/Reg/IsRegistrationCancelRequested/"), { params });
  }

  cancelRegistration(regId: number): Observable<ResultWrapper<UserAccountRegDto>> {
    const params = new HttpParams().append('regId', regId.toString());
    return this.http.get<ResultWrapper<UserAccountRegDto>>(this.configHelper.getApiUrl("User/Reg/RequestCancel/"), { params });
  }

  getTargetRegTransferSessions(sourceRegId: number, regionId: number): Observable<ResultWrapper<TestSessionForRegistrationDto[]>> {
    const params = new HttpParams()
      .set('sourceRegId', sourceRegId.toString())
      .set('regionId', regionId.toString());
    return this.http.get<ResultWrapper<TestSessionForRegistrationDto[]>>(this.configHelper.getApiUrl("User/Reg/Transfer/TargetSessions"), { params });
  }

  getRegTransferPayment(sourceRegId: number, targetTestSessionId: number): Observable<ResultWrapper<RegTransferPaymentDto>> {
    const params = new HttpParams()
      .set('sourceRegId', sourceRegId.toString())
      .set('targetTestSessionId', targetTestSessionId.toString());
    return this.http.get<ResultWrapper<RegTransferPaymentDto>>(this.configHelper.getApiUrl("User/Reg/Transfer/Payment"), { params });
  }

  transferReg(request: UserTestRegistrationTransferRequest): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Reg/Transfer"), request);
  }

  establishProductAudioLinks(productId: number): Observable<ResultWrapper<string[]>> {
    const params = new HttpParams().set('productId', productId.toString());
    return this.http.get<ResultWrapper<string[]>>(this.configHelper.getApiUrl("User/ProductAudioFileNames"), { params });
  }

  getWebDeliveryRegStatus(regId: number): Observable<ResultWrapper<WebDeliveryTestTakerStateDto>> {
    const params = new HttpParams().set('regId', regId.toString());
    return this.http.get<ResultWrapper<WebDeliveryTestTakerStateDto>>(this.configHelper.getApiUrl("WebDeliveryTestTakerState"), { params });
  }

  webDeliveryStartTestAttempt(regId: number): Observable<ResultWrapper<string>> {
    return this.http.post<ResultWrapper<string>>(this.configHelper.getApiUrl("User/Reg/WebDeliveryStartTestAttempt") + "?regId=" + regId.toString(), {});
  }

  getScoreRecipientExpressShippings(regId: number): Observable<ResultWrapper<ScoreRecipientExpressShippingDto[]>> {
    return this.http.get<ResultWrapper<ScoreRecipientExpressShippingDto[]>>(this.configHelper.getApiUrl("User/Reg/ScoreRecipientExpressShippings/") + regId.toString(), {});
  }

  InvalidateAccessToken(): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(this.configHelper.getApiUrl("InvalidateAccessToken"), {});
  }
}
